import algoliasearch from 'algoliasearch'
import type { SearchClient, SearchIndex } from 'algoliasearch'
import aa from 'search-insights'
import { AlgoliaKeyError, getAlgoliaAPIKey } from '@/search/api'
import type { AlgoliaAPIKey, AlgoliaIndices } from '@/search/search_types'

export const KEY_EXPIRY_BUFFER_MS = 60 * 1000
export const KEY_FETCH_ATTEMPTS = 3
export const KEY_FETCH_BACKOFF_MS = 500

const HTTP_BAD_REQUEST = 400
const HTTP_FORBIDDEN = 403

export interface AlgoliaClientOptions {
  appID: string
  featureName: string
  indices: AlgoliaIndices
}

type MultipleQueriesArgs = Parameters<SearchClient['multipleQueries']>

// The subset of a search client that instantsearch calls, routed through our key handling
// so a page never holds a key that expires underneath it.
export interface KeyManagedSearchClient {
  search: (...args: MultipleQueriesArgs) => ReturnType<SearchClient['multipleQueries']>
}

export class AlgoliaClient {
  readonly appID: string
  readonly featureName: string
  readonly indices: AlgoliaIndices
  placeholderUserToken = ''
  client: SearchClient | null = null
  index: SearchIndex | null = null
  apiKey: string | null = null
  keyDeadline: number | null = null
  private analyticsInitialized = false
  private pendingRefresh: Promise<SearchIndex> | null = null

  constructor({ appID, featureName, indices }: AlgoliaClientOptions) {
    this.appID = appID
    this.featureName = featureName
    this.indices = indices
  }

  static async create(options: AlgoliaClientOptions): Promise<AlgoliaClient> {
    const algoliaClient = new AlgoliaClient(options)
    await algoliaClient.refreshIndex()
    return algoliaClient
  }

  async search<THit = unknown>(...args: Parameters<SearchIndex['search']>) {
    await this.checkApiKeyValidity()
    try {
      return await this.requireIndex().search<THit>(...args)
    } catch (e) {
      await this.refreshIfKeyRejected(e)
      return this.requireIndex().search<THit>(...args)
    }
  }

  async multipleQueries<THit = unknown>(...args: MultipleQueriesArgs) {
    await this.checkApiKeyValidity()
    try {
      return await this.requireClient().multipleQueries<THit>(...args)
    } catch (e) {
      await this.refreshIfKeyRejected(e)
      return this.requireClient().multipleQueries<THit>(...args)
    }
  }

  asSearchClient(): KeyManagedSearchClient {
    return { search: (...args) => this.multipleQueries(...args) }
  }

  async checkApiKeyValidity() {
    if (this.shouldRefresh()) {
      await this.refreshIndex()
    }
  }

  shouldRefresh(): boolean {
    if (!this.client || !this.apiKey || this.keyDeadline === null) {
      return true
    }
    return this.keyDeadline - Date.now() <= KEY_EXPIRY_BUFFER_MS
  }

  refreshIndex(): Promise<SearchIndex> {
    this.pendingRefresh ??= this.fetchKeyWithRetries().finally(() => {
      this.pendingRefresh = null
    })
    return this.pendingRefresh
  }

  getIndexName(): string {
    return Array.isArray(this.indices) ? this.indices[0] : this.indices
  }

  async initAnalytics() {
    aa('init', {
      appId: this.appID,
      apiKey: await this.getAPIKey(),
      // Without a cookie, Algolia Insights cannot generate a user token for signed-out visitors
      useCookie: true
    })
    this.analyticsInitialized = true

    aa('getUserToken', null, (err, newUserToken) => {
      if (err) {
        console.error(err)
        return
      }
      this.placeholderUserToken = String(newUserToken)
    })
  }

  async getAPIKey(): Promise<string> {
    await this.checkApiKeyValidity()
    return this.apiKey as string
  }

  getPlaceholderUserToken(): string {
    return this.placeholderUserToken
  }

  private async refreshIfKeyRejected(error: unknown) {
    if (!isKeyRejectedError(error)) {
      throw error
    }
    await this.refreshIndex()
  }

  private async fetchKeyWithRetries(): Promise<SearchIndex> {
    for (let attempt = 1; ; attempt++) {
      try {
        return this.useKey(await getAlgoliaAPIKey(this.featureName, this.indices))
      } catch (e) {
        const canRetry = e instanceof AlgoliaKeyError && e.isTransient && attempt < KEY_FETCH_ATTEMPTS
        if (!canRetry) {
          throw new Error(`Could not fetch an Algolia API key after ${attempt} attempt(s): ${errorMessage(e)}`, { cause: e })
        }
        await delay(KEY_FETCH_BACKOFF_MS * 2 ** (attempt - 1))
      }
    }
  }

  private useKey({ key, expiration, expires_in_seconds }: AlgoliaAPIKey): SearchIndex {
    this.apiKey = key
    this.keyDeadline = expires_in_seconds === undefined
      ? new Date(expiration).getTime()
      : Date.now() + expires_in_seconds * 1000
    this.client = algoliasearch(this.appID, key)
    this.index = this.client.initIndex(this.getIndexName())
    if (this.analyticsInitialized) {
      aa('init', { partial: true, apiKey: key })
    }
    return this.index
  }

  private requireIndex(): SearchIndex {
    if (!this.index) {
      throw new Error('Algolia index used before it was initialized')
    }
    return this.index
  }

  private requireClient(): SearchClient {
    if (!this.client) {
      throw new Error('Algolia client used before it was initialized')
    }
    return this.client
  }
}

// Algolia answers an expired secured key with 400 "validUntil parameter expired" and an
// invalid one with 403. Anything else — a network failure, a malformed query — is not
// cured by a new key, so it is not worth a key fetch.
function isKeyRejectedError(error: unknown): boolean {
  const { name, status, message } = (error ?? {}) as { name?: string, status?: number, message?: string }
  if (name !== 'ApiError') {
    return false
  }
  return status === HTTP_FORBIDDEN || (status === HTTP_BAD_REQUEST && /validUntil/.test(message ?? ''))
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : JSON.stringify(error)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
